import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import useChainProviderHook from "@/hooks/useChainProvider"
import { getSportName, getSportSlug } from "../utils/transformSxMarkets"
import type { SxSummaryFixture, SxSummaryResponse } from "../types/markets"
import { useSxSportsNavigation } from "./useSxSportsNavigation"
import { SX_CONFIG } from "../constants"
import { buildUrl } from "../api"
import { calculateOdds } from "./useSXMarketData"
import { hasGameStarted, isAfter48Hours } from "@/utils/helper"

// Result interface for the hook
interface UseSXPopularMarketsResult {
  markets: any[]
  marketsCount: number
  isLoading: boolean
  isFetching: boolean // For live switch transitions
  error: Error | null
}

/**
 * Helper function to fetch popular markets summary
 */
const fetchPopularSummary = async (
  chainId: number
): Promise<SxSummaryResponse> => {
  const TOKEN = SX_CONFIG[chainId].CONTRACTS.TOKEN

  const url = buildUrl(`/summary/popular/${TOKEN}`, {
    chainId: chainId.toString(),
  })
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const result: SxSummaryResponse = await response.json()
  if (result.status !== "success") {
    throw new Error(`API error: ${result.status}`)
  }

  return result
}

/**
 * Hook for fetching SX popular markets from the summary/popular API
 */
const useSXPopularMarkets = (
  enabled: boolean = true
): UseSXPopularMarketsResult => {
  const { chainId } = useChainProviderHook()
  // const { data: navData } = useSxSportsNavigation()

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["sx-popular-markets", chainId],
    queryFn: async () => await fetchPopularSummary(chainId),
    enabled: enabled && !!chainId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  })

  // Process the data using the same transform function as other hooks
  const processedData = useMemo(() => {
    if (!data?.data?.sports) {
      return {
        sports: [],
        fixtures: [],
        markets: [],
        marketsCount: 0,
      }
    }

    const fixtures: SxSummaryFixture[] = []
    const validFixtures: SxSummaryFixture[] = []

    const marketMap = new Map<string, any>()
    // Collect fixtures and extract markets
    data.data.sports.forEach(sport => {
      sport.leagues.forEach(league => {
        league.fixtures.forEach(fixture => {
          fixtures.push(fixture)

          const isLocked = isAfter48Hours(fixture.gameTime)
          const isGameStarted = hasGameStarted(fixture.gameTime)
          if (isLocked || isGameStarted) {
            return // skip markets with future start date
          }

          const hasValidMarkets =
            fixture.markets &&
            fixture.markets.some(
              market => market.bestOddsOutcomeOne && market.bestOddsOutcomeTwo
            )

          if (hasValidMarkets) {
            validFixtures.push(fixture)
          }

          const [foundGroup] = fixture.betGroups

          fixture.markets.forEach(market => {
            if (!market.bestOddsOutcomeOne || !market.bestOddsOutcomeTwo) {
              return
            }

            const gameLabel = `${fixture.teamOne} - ${fixture.teamTwo}`
            const sportsSlug =
              getSportSlug(sport.sportId) === "mixed-martial-arts"
                ? "mma"
                : getSportSlug(sport.sportId)

            const sportsName =
              getSportName(sport.sportId) === "Mixed Martial Arts"
                ? "MMA"
                : getSportName(sport.sportId)

            // 👇 build unique key
            const key = `${fixture.leagueId}-${fixture.eventId}`

            if (!marketMap.has(key)) {
              marketMap.set(key, {
                sportId: sport.sportId,
                leagueLabel: league.leagueLabel,
                gameLabel,
                sportsSlug,
                sportsName,
                betGroup: foundGroup,
                marketType: market.marketType,
                outcomeOneName: market.outcomeOneName,
                outcomeTwoName: market.outcomeTwoName,
                providerMarketType: market.marketType,
                gameTime: fixture.gameTime,
                fixtureId: fixture.id,
                market: {
                  marketHash: market.marketHash,
                  baseToken: SX_CONFIG[chainId].CONTRACTS.TOKEN,
                  bestOddsOutcomeOne: market.bestOddsOutcomeOne,
                  bestOddsOutcomeTwo: market.bestOddsOutcomeTwo,
                  outcomeOneOdds: calculateOdds(
                    Number(market.bestOddsOutcomeOne)
                  ),
                  outcomeTwoOdds: calculateOdds(
                    Number(market.bestOddsOutcomeTwo)
                  ),
                },
              })
            }
          })
        })
      })
    })

    const uniqueMarkets = Array.from(marketMap.values())

    return {
      markets: uniqueMarkets,
      marketsCount: validFixtures.length, // Count only fixtures with valid markets
    }
  }, [data])

  return {
    markets: processedData.markets,
    marketsCount: processedData.marketsCount,
    isLoading,
    isFetching,
    error: error as Error | null,
  }
}

export default useSXPopularMarkets
